import { useCallback, useEffect, useState } from 'react';
import dayjs from 'dayjs';
import timezonePlugin from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { useInterval } from '@frontend/design-system';
import { useCalendarGridStore } from '../../../store/calendar-grid.store';
import { timezoneToTimeIndicatorInfo } from '../../../utils';
import { TimeIndicator } from './time-indicator';

dayjs.extend(utc);
dayjs.extend(timezonePlugin);

const TIME_INDICATOR_UPDATE_INTERVAL = 60000; // Update every minute
const DEFAULT_TIMEZONE = dayjs.tz.guess();

export const useTodayTimeIndicator = (timezone = DEFAULT_TIMEZONE) => {
  const { calendarDate, timeIndicatorLines } = useCalendarGridStore(['calendarDate', 'timeIndicatorLines']);

  const isToday = timeIndicatorLines !== 'none' && calendarDate === dayjs().tz(timezone).format('MM/DD/YYYY');

  const [timeIndicatorInfo, setTimeIndicatorInfo] = useState<[string, number]>();

  useEffect(() => {
    if (isToday) {
      const info = timezoneToTimeIndicatorInfo(timezone);
      if (info) setTimeIndicatorInfo(info);
    }
  }, [isToday, timezone]);

  const update = useCallback(() => {
    const info = timezoneToTimeIndicatorInfo(timezone);
    if (info) setTimeIndicatorInfo(info);
  }, [timezone]);

  useInterval(update, isToday ? TIME_INDICATOR_UPDATE_INTERVAL : null);

  return isToday ? timeIndicatorInfo : undefined;
};

interface Props {
  gridColumn: string;
  timezone?: string;
}

export const TodayTimeIndicator = ({ gridColumn, timezone }: Props) => {
  const timeIndicatorInfo = useTodayTimeIndicator(timezone);
  if (!timeIndicatorInfo) return null;
  const [currentTime, rowNumber] = timeIndicatorInfo;

  return <TimeIndicator gridColumn={gridColumn} rowNumber={rowNumber} label={currentTime} />;
};
